import { Alert, Box, Button, Typography } from "@mui/material";
import { Liquid } from "liquidjs";
import { LayoutTemplate } from "lucide-react";
import mjmlToHtml from "mjml-browser";
import { useState } from "react";
import {
  Create,
  Datagrid,
  DateField,
  Edit,
  List,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  required,
  useNotify,
} from "react-admin";
import { useWatch } from "react-hook-form";
import { EditorToolbar } from "../../components";
import { AceInput, bulkEmailResources, parseJson, validJson } from "./common";

const disabledFileSystem = {
  exists: async () => false,
  existsSync: () => false,
  readFile: async () => {
    throw new Error("Liquid includes are disabled");
  },
  readFileSync: () => {
    throw new Error("Liquid includes are disabled");
  },
  resolve: () => {
    throw new Error("Liquid includes are disabled");
  },
  contains: () => false,
};

// Keep these options aligned with the production renderer in temporal/src/lib/bulk-email.ts; update both renderers together.
const liquidOptions = {
  strictFilters: true,
  strictVariables: true,
  ownPropertyOnly: true,
  parseLimit: 250_000,
  renderLimit: 1_000,
  memoryLimit: 10_000_000,
  fs: disabledFileSystem,
  dynamicPartials: false,
  relativeReference: false,
};

const subjectEngine = new Liquid(liquidOptions);
const bodyEngine = new Liquid({ ...liquidOptions, outputEscape: "escape" });

export async function renderBulkEmailTemplatePreview({
  format,
  subject,
  body,
  recipient,
}) {
  const context = {
    recipient,
    campaign: {
      id: 0,
      name: "Template preview",
      description: "Template preview",
    },
    sending: {
      id: 0,
      name: "Template preview",
      from_address: "TorahAnytime <info@torahanytime.com>",
      reply_to_address: "info@torahanytime.com",
      audience_mode: "snapshot",
      overlap_mode: "first",
    },
    system: {
      email_preferences_url:
        "https://api.torahanytime.com/bulk-email/unsubscribe?token=preview",
    },
  };
  const renderedSubject = await subjectEngine.parseAndRender(subject, context);
  if (/\r|\n/.test(renderedSubject)) {
    throw new Error("Rendered subjects cannot contain line breaks");
  }

  const renderedBody = await bodyEngine.parseAndRender(body, context);
  if (format === "html") {
    return { subject: renderedSubject, html: renderedBody };
  }

  const compiled = mjmlToHtml(renderedBody);
  if (compiled.errors.length) {
    throw new Error(
      `MJML compilation failed: ${compiled.errors
        .map((error) => error.formattedMessage)
        .join("; ")}`,
    );
  }
  return { subject: renderedSubject, html: compiled.html };
}

function TemplateList() {
  return (
    <List sort={{ field: "id", order: "DESC" }} perPage={50}>
      <Datagrid rowClick="edit">
        <TextField source="id" />
        <TextField source="name" />
        <TextField source="format" />
        <TextField source="subject" />
        <DateField source="updated_at" showTime />
      </Datagrid>
    </List>
  );
}

function TemplatePreview() {
  const notify = useNotify();
  const [preview, setPreview] = useState();
  const [error, setError] = useState();
  const [format, subject, body, sampleData] = useWatch({
    name: ["format", "subject", "body", "sample_data_json"],
  });

  const render = async () => {
    try {
      setError(undefined);
      setPreview(
        await renderBulkEmailTemplatePreview({
          format,
          subject,
          body,
          recipient: parseJson(sampleData),
        }),
      );
    } catch (renderError) {
      setPreview(undefined);
      const message = renderError.message;
      setError(message);
      notify(message, { type: "error" });
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1 }}>
        <Button type="button" variant="contained" onClick={render}>
          Render preview
        </Button>
        {preview?.subject && (
          <Typography>
            <strong>Subject:</strong> {preview.subject}
          </Typography>
        )}
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <iframe
        title="Rendered template preview"
        sandbox=""
        srcDoc={preview?.html || ""}
        style={{ width: "100%", height: "65vh", border: "1px solid #ccc" }}
      />
    </Box>
  );
}

function TemplateFields() {
  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "minmax(220px, 1fr) 180px minmax(300px, 2fr)",
          gap: 2,
        }}
      >
        <TextInput source="name" validate={required()} />
        <SelectInput
          source="format"
          validate={required()}
          choices={[
            { id: "mjml", name: "MJML" },
            { id: "html", name: "HTML" },
          ]}
        />
        <TextInput
          source="subject"
          label="Liquid subject"
          validate={required()}
        />
      </Box>
      <Typography sx={{ width: "100%" }}>
        You can use recipient.*, campaign.*, and sending.* variables in this
        template. To add an unsubscribe or “Manage email preferences” link, use{" "}
        {"{{ system.email_preferences_url }}"} as the link URL. You control the
        link’s wording, appearance, and placement. When a recipient follows the
        link, they can stop emails from this Campaign or stop all TorahAnytime
        email campaigns.
      </Typography>
      <AceInput
        source="sample_data_json"
        label="Preview recipient JSON"
        mode="json"
        height="180px"
        validate={validJson}
      />
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          mt: 1,
        }}
      >
        <AceInput
          source="body"
          label="Liquid template source"
          mode="html"
          height="65vh"
          validate={required()}
        />
        <TemplatePreview />
      </Box>
    </>
  );
}

// sample_data_json is editor-only state; the API persists the parsed JSON object.
function transformTemplate(data) {
  const { sample_data_json: sampleData, ...template } = data;
  return { ...template, sample_data: parseJson(sampleData) };
}

// Put the API's sample_data into form state so preview watchers can use it before the editor is changed.
export function getTemplateDefaultValues(record) {
  return {
    format: "mjml",
    sample_data_json: JSON.stringify(record?.sample_data || {}, null, 2),
  };
}

function TemplateCreate() {
  return (
    <Create
      redirect="edit"
      mutationMode="pessimistic"
      transform={transformTemplate}
    >
      <SimpleForm defaultValues={getTemplateDefaultValues}>
        <TemplateFields />
      </SimpleForm>
    </Create>
  );
}

function TemplateEdit() {
  return (
    <Edit mutationMode="pessimistic" transform={transformTemplate}>
      <SimpleForm
        defaultValues={getTemplateDefaultValues}
        toolbar={<EditorToolbar showDelete />}
      >
        <TemplateFields />
      </SimpleForm>
    </Edit>
  );
}

export const BulkEmailTemplates = {
  category: "email",
  name: bulkEmailResources.templates,
  list: TemplateList,
  create: TemplateCreate,
  edit: TemplateEdit,
  icon: LayoutTemplate,
  roles: ["administrator"],
  options: { label: "Email Templates (Bulk)" },
};
